
import { useToast } from '@/hooks/use-toast'
import { useAdminTasks } from './useAdminTasks'
import { useSchoolTasks } from './useSchoolTasks'
import { useUserTasks } from './useUserTasks'
import { useCustomTasks } from './useCustomTasks'
import { useTasksRealtime } from './useTasksRealtime'
import { addTaskToMyTasks, updateTaskStatus } from '@/utils/taskOperations'
import { getDiscoverTasks, getMyTasks, getPriorityTasks, getArchivedTasks } from '@/utils/taskFilters'

export const useTasks = () => {
  const { toast } = useToast()
  
  const { adminTasks, loading: adminLoading, refreshAdminTasks } = useAdminTasks()
  const { schoolTasks, loading: schoolLoading, refreshSchoolTasks } = useSchoolTasks()
  const { userTasks, loading: userLoading, refreshUserTasks } = useUserTasks()
  const { customTasks, loading: customLoading, createCustomTask, refreshCustomTasks } = useCustomTasks()

  const loading = adminLoading || schoolLoading || userLoading || customLoading

  // Set up real-time updates for all task-related changes
  useTasksRealtime({
    onUserTasksChange: refreshUserTasks,
    onCustomTasksChange: refreshCustomTasks,
    onAdminTasksChange: refreshAdminTasks,
    onSchoolTasksChange: refreshSchoolTasks
  })

  const refreshTasks = () => {
    refreshAdminTasks()
    refreshSchoolTasks()
    refreshUserTasks()
    refreshCustomTasks()
  }

  const handleAddTaskToMyTasks = async (taskId: string, taskSource: 'admin' | 'school' = 'school') => {
    const success = await addTaskToMyTasks(taskId, taskSource, toast)
    if (success) {
      // Force immediate refresh to ensure UI updates
      refreshTasks()
    }
    return success
  }

  const handleUpdateTaskStatus = async (taskId: string, status: string, isCustomTask = false) => {
    const success = await updateTaskStatus(taskId, status, isCustomTask, toast)
    if (success) {
      // Force immediate refresh to ensure UI updates
      refreshTasks()
    }
    return success
  }

  return {
    schoolTasks,
    adminTasks,
    userTasks,
    customTasks,
    loading,
    addTaskToMyTasks: handleAddTaskToMyTasks,
    createCustomTask,
    updateTaskStatus: handleUpdateTaskStatus,
    getDiscoverTasks: () => getDiscoverTasks(schoolTasks, adminTasks, userTasks),
    getMyTasks: () => getMyTasks(userTasks, customTasks),
    getPriorityTasks: () => getPriorityTasks(userTasks, customTasks),
    getArchivedTasks: () => getArchivedTasks(userTasks, customTasks),
    refreshTasks
  }
}
